import api from './api';
import { API_ENDPOINTS } from '../config/api';

/**
 * Order Service
 * Handles order creation, history, and tracking
 * Supports both logged-in and guest users
 */
export const orderService = {
  /**
   * Create a new order from cart
   * @param {Object} orderData - Order creation data
   * @param {Object} orderData.shipping_address - Shipping address details
   * @param {Object} [orderData.billing_address] - Billing address (optional, defaults to shipping)
   * @param {string} orderData.payment_method - 'COD' or 'RAZORPAY'
   * @param {string} [orderData.guest_email] - Guest email (required for guests)
   * @param {string} [orderData.guest_phone] - Guest phone (required for guests)
   * @param {string} [orderData.guest_name] - Guest name (required for guests)
   * @param {string} [orderData.notes] - Order notes
   * @returns {Promise<Object>} Created order details
   */
  createOrder: async (orderData) => {
    try {
      const response = await api.post(API_ENDPOINTS.ORDERS.CREATE, orderData);

      if (response.data.success) {
        return {
          success: true,
          message: response.data.message,
          order: response.data.order
        };
      }

      return {
        success: false,
        error: response.data.error || 'Failed to create order'
      };
    } catch (error) {
      console.error('Error creating order:', error);

      // Handle validation errors
      if (error.response?.status === 400) {
        const details = error.response.data?.details;

        // Handle different error formats
        if (typeof details === 'string') {
          return {
            success: false,
            error: details
          };
        }

        if (Array.isArray(details)) {
          return {
            success: false,
            error: details.join(', ')
          };
        }

        if (typeof details === 'object') {
          // Format field errors
          const errorMessages = [];
          Object.keys(details).forEach(key => {
            if (Array.isArray(details[key])) {
              errorMessages.push(`${key}: ${details[key].join(', ')}`);
            } else {
              errorMessages.push(`${key}: ${details[key]}`);
            }
          });
          return {
            success: false,
            error: errorMessages.join('; ') || error.response.data?.error || 'Validation failed'
          };
        }

        return {
          success: false,
          error: error.response.data?.error || 'Invalid order data'
        };
      }

      return {
        success: false,
        error: error.response?.data?.error || 'Failed to create order. Please try again.'
      };
    }
  },

  /**
   * Get order history for current user/guest
   * @param {number} [page=1] - Page number
   * @param {number} [pageSize=20] - Items per page
   * @returns {Promise<Object>} Paginated order list
   */
  getOrderHistory: async (page = 1, pageSize = 20) => {
    try {
      const response = await api.get(API_ENDPOINTS.ORDERS.HISTORY, {
        params: { page, page_size: pageSize }
      });

      const payload = response.data || {};
      if (payload.success === false) {
        return {
          success: false,
          error: payload.error || 'Failed to fetch orders',
          orders: []
        };
      }

      const orders = Array.isArray(payload.results)
        ? payload.results
        : payload.orders || [];
      const count = payload.count ?? orders.length;
      const hasMore = Boolean(payload.next) || orders.length === pageSize;

      return {
        success: true,
        orders,
        count,
        hasMore
      };
    } catch (error) {
      console.error('Error fetching order history:', error);

      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.detail || 'Failed to fetch orders',
        orders: []
      };
    }
  },

  /**
   * Get detailed information about a specific order
   * @param {string} orderNumber - Unique order number (e.g., "ORD-1734192000-A1B2C3D4")
   * @returns {Promise<Object>} Order details with items and delivery info
   */
  getOrderDetails: async (orderNumber) => {
    try {
      const response = await api.get(`${API_ENDPOINTS.ORDERS.DETAIL}/${orderNumber}/`);

      if (response.data.success) {
        return {
          success: true,
          order: response.data.order,
          deliveryInfo: response.data.delivery_info
        };
      }

      return {
        success: false,
        error: response.data.error || 'Failed to fetch order details'
      };
    } catch (error) {
      console.error('Error fetching order details:', error);

      if (error.response?.status === 404) {
        return {
          success: false,
          error: 'Order not found or access denied'
        };
      }

      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch order details'
      };
    }
  },

  /**
   * Get tracking information for an order
   * @param {string} orderNumber - Unique order number
   * @returns {Promise<Object>} Tracking details
   */
  getOrderTracking: async (orderNumber) => {
    try {
      const response = await api.get(`${API_ENDPOINTS.ORDERS.DETAIL}/${orderNumber}/tracking/`);

      if (response.data.success) {
        return {
          success: true,
          tracking: response.data.tracking
        };
      }

      return {
        success: false,
        message: response.data.message || 'Tracking information not available'
      };
    } catch (error) {
      console.error('Error fetching order tracking:', error);

      return {
        success: false,
        message: 'Tracking information not available yet'
      };
    }
  },

  /**
   * Build order data object from checkout form
   * @param {Object} formData - Checkout form data
   * @param {boolean} isAuthenticated - Whether user is logged in
   * @returns {Object} Formatted order data for API
   */
  buildOrderData: (formData, isAuthenticated) => {
    const orderData = {
      shipping_address: {
        address_line1: formData.shippingAddress.addressLine1,
        address_line2: formData.shippingAddress.addressLine2 || '',
        city: formData.shippingAddress.city,
        state: formData.shippingAddress.state,
        pincode: formData.shippingAddress.pincode,
        phone: formData.shippingAddress.phone
      },
      payment_method: formData.paymentMethod,
      notes: formData.notes || ''
    };

    // Add billing address if different from shipping
    if (!formData.sameAsBilling && formData.billingAddress) {
      orderData.billing_address = {
        address_line1: formData.billingAddress.addressLine1,
        address_line2: formData.billingAddress.addressLine2 || '',
        city: formData.billingAddress.city,
        state: formData.billingAddress.state,
        pincode: formData.billingAddress.pincode,
        phone: formData.billingAddress.phone
      };
    }

    // Add guest info if not authenticated
    if (!isAuthenticated) {
      orderData.guest_email = formData.guestInfo.email;
      orderData.guest_phone = formData.guestInfo.phone;
      orderData.guest_name = formData.guestInfo.name;
    }

    return orderData;
  },

  /**
   * Get order status display info
   * @param {string} status - Order status code
   * @returns {Object} Status display info with label and color
   */
  getStatusDisplay: (status) => {
    const statusMap = {
      PENDING: { label: 'Pending', color: '#6b7280', bgColor: '#f3f4f6' },
      PAYMENT_INITIATED: { label: 'Payment Initiated', color: '#f59e0b', bgColor: '#fef3c7' },
      PAYMENT_FAILED: { label: 'Payment Failed', color: '#ef4444', bgColor: '#fee2e2' },
      CONFIRMED: { label: 'Confirmed', color: '#16a34a', bgColor: '#dcfce7' },
      PROCESSING: { label: 'Processing', color: '#8b5cf6', bgColor: '#ede9fe' },
      SHIPPED: { label: 'Shipped', color: '#2563eb', bgColor: '#dbeafe' },
      DELIVERED: { label: 'Delivered', color: '#15803d', bgColor: '#dcfce7' },
      CANCELLED: { label: 'Cancelled', color: '#ef4444', bgColor: '#fee2e2' },
      REFUNDED: { label: 'Refunded', color: '#6b7280', bgColor: '#f3f4f6' }
    };

    return statusMap[status] || { label: status, color: '#6b7280', bgColor: '#f3f4f6' };
  },

  getPaymentMethodDisplay: (order) => {
    if (order?.payment_method === 'COD') return 'Cash on Delivery';

    const confirmedOrLater = ['CONFIRMED', 'PROCESSING', 'SHIPPED', 'DELIVERED'].includes(order?.status);
    if (confirmedOrLater) return 'Paid Online';
    if (order?.status === 'PAYMENT_FAILED') return 'Online payment failed';
    if (['PENDING', 'PAYMENT_INITIATED'].includes(order?.status)) return 'Online payment pending';
    return 'Online payment';
  },

  /**
   * Format order date for display
   * @param {string} dateString - ISO date string
   * @returns {string} Formatted date
   */
  formatOrderDate: (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
};

export default orderService;
